import type { MemoryStore } from '@ownyou/memory-store';
import {
  computeReputationLevel,
  createBLS12381Suite,
  issueCredential,
  loadDisclosurePolicy,
} from '@ownyou/bbs-plus';
import type {
  DisclosurePolicy,
  OwnYouVerifiableCredential,
  ReputationLevel,
  ReputationPolicy,
} from '@ownyou/bbs-plus';
import { KeyDerivation } from '@ownyou/identity';
import type { WalletSigner } from '@ownyou/identity';
import { groupingToField } from '@ownyou/iab-classifier/canonical-demographic-keys';
import bundledDisclosurePolicy from '@ownyou/bbs-plus/data/disclosure-policy-v3.json' with { type: 'json' };
import bundledReputationPolicy from '@ownyou/bbs-plus/data/reputation-policy-v1.json' with { type: 'json' };
import {
  paginateClassifications,
  projectToClaimSet,
  runSelectionPipeline,
  type DisclosureFactRecord,
} from './wire-builder/selection-pipeline';

const DISCLOSURE_POLICY: DisclosurePolicy = loadDisclosurePolicy(bundledDisclosurePolicy);
const REPUTATION_POLICY: ReputationPolicy = bundledReputationPolicy as ReputationPolicy;

/**
 * Slice 0 / AT-13 (ADR 0005 Q5): canonical taxonomy declaration that the
 * wire-builder stamps on every emitted audience-segment claim. Derived
 * mechanically from `policy.canonical_iab_taxonomy` so a future policy
 * spec/version swap doesn't require a code change here.
 */
function deriveCanonicalTaxonomy(policy: DisclosurePolicy): string {
  const tax = policy.canonical_iab_taxonomy;
  return `iab-${tax.spec}-${tax.version}`;
}

const CANONICAL_TAXONOMY = deriveCanonicalTaxonomy(DISCLOSURE_POLICY);

export interface NormalizedClaim {
  category: string;
  confidence: number;
  /**
   * Slice 0 / AT-13 (ADR 0005 Q5): every audience-segment claim
   * (`interests` / `purchase_intent`) carries the taxonomy declaration
   * that's cryptographically bound into the BBS+ 5-tuple. The wire-builder
   * sources this from `policy.canonical_iab_taxonomy` (currently
   * `iab-audience-1.1`) so the wallet can validate against
   * `policy.allowed_taxonomies` before the signed credential is produced.
   * The full multi-stage selection pipeline lands in AT-16.
   */
  taxonomy?: string;
}

export interface NormalizedClaimSet {
  interests: NormalizedClaim[];
  purchase_intent: NormalizedClaim[];
  demographics: Record<string, string>;
  /**
   * Slice 0 / AT-14 (ADR 0005 Q2 + Q3): per-key confidence sidecar for
   * demographic claims. The wire-builder populates this from the source
   * `DisclosureFact.confidence` in `NS.iabClassifications`. The encoder
   * (`@ownyou/bbs-plus :: claimsToMessages`) emits an `attribute:v1`
   * 5-tuple ONLY for demographic keys with a finite confidence in
   * `[0, 1]` here.
   *
   * AT-16 replaces this raw observation confidence with the proper
   * multi-stage `disclosure_confidence` aggregation. Until then, the
   * raw observation confidence is plumbed through verbatim — the
   * minimum incremental change to enable the AT-14 cryptographic
   * confidence-binding tracer.
   */
  demographics_confidence?: Record<string, number>;
  household: Record<string, string>;
  reputation_level: ReputationLevel;
}

/**
 * Data-only portion of the sync payload — produced by
 * `buildExtensionCredentialSyncPayload`, which reads from the Store but never
 * touches the wallet. The wallet identity material (nymSecret, bbsKeyPair,
 * bbsSignature) is added by `syncCredentialsToExtension` after derivation
 * via the wallet signer; downstream consumers see the full
 * `ExtensionCredentialSyncPayload`.
 */
export interface ExtensionCredentialSyncBasePayload {
  claimSet: NormalizedClaimSet;
  credentials?: OwnYouVerifiableCredential[];
  preferences: {
    excluded_categories: string[];
    demographics_granularity: 'off' | 'standard';
    reputation_enabled: boolean;
  };
  disclosurePolicy: DisclosurePolicy;
}

export interface ExtensionCredentialSyncPayload extends ExtensionCredentialSyncBasePayload {
  /**
   * Wallet identity material derived deterministically from the user's wallet
   * signer (Sprint 18c remediation, closes G2). The extension stores these
   * verbatim and refuses to fabricate them — see browser-extension/modules/
   * wallet-module.js. Encoded as `number[]` for chrome.runtime structured
   * clone; the wallet-module rehydrates back to Uint8Array on receipt.
   */
  nymSecret: number[];
  bbsKeyPair: { secretKey: number[]; publicKey: number[] };
  bbsSignature: number[];
}

export interface ExtensionCredentialSyncStatus {
  state: 'disabled' | 'unavailable' | 'synced' | 'error';
  message?: string;
  syncedAt?: number;
  claimCount?: number;
}

declare global {
  interface Window {
    OwnYouCredentialSync?: {
      push(payload: ExtensionCredentialSyncPayload): Promise<{ success?: boolean; error?: string }>;
      getStatus(): Promise<{ success?: boolean; hasKeys?: boolean; credentialCount?: number; claimSetUpdatedAt?: number | null; error?: string }>;
    };
  }
}

const STATUS_KEY = 'ownyou_extension_credential_sync_status';
const PRIVACY_KEY = 'ownyou_privacy_toggles';
const SYNC_DIGEST_PREFIX = 'ownyou_extension_credential_sync_digest:';

export function isBbsSharingEnabled(storage: Storage = localStorage): boolean {
  try {
    const saved = storage.getItem(PRIVACY_KEY);
    if (!saved) return false;
    const parsed = JSON.parse(saved) as Record<string, unknown>;
    return parsed.bbs === true;
  } catch {
    return false;
  }
}

/**
 * Audience Taxonomy validator (Slice 0 / AT-05).
 *
 * The wire-builder produces `claimSet.interests` and `claimSet.purchase_intent`
 * entries whose `category` is a numeric-string IAB Audience Taxonomy 1.1 ID
 * (`segtax=4`, per the OpenRTB community extension). The strict reader at
 * `pickCategory` validates `item.taxonomy_id` against `idPattern`: numeric,
 * leading non-zero digit (rejects 0), at most `maxIdLength` digits.
 *
 * Pre-Slice-0 records carrying a string `category` like `'IABxx'` or a free-text
 * label like `'Sports & Outdoors'` are dropped: they have no `taxonomy_id`
 * field at all, so the strict reader returns null. This replaces the F3-style
 * regex-on-category-string filter inherited from Sprint 18c with a positive
 * shape check on the structured-record field the reconciler actually emits.
 */
export interface AudienceTaxonomyValidatorConfig {
  idPattern: RegExp;
  maxIdLength: number;
}

export const DEFAULT_AUDIENCE_TAXONOMY_VALIDATOR: AudienceTaxonomyValidatorConfig = {
  idPattern: /^[1-9][0-9]{0,5}$/,
  maxIdLength: 6,
};

// Slice 0 / AT-16: the `KnownSection` discriminant + `KNOWN_SECTIONS` set
// previously used by the legacy section-router are now owned by the
// pipeline (`applyDropAndShapeFilter` performs the structural gate).

export async function buildExtensionCredentialSyncPayload(
  store: MemoryStore,
  userId: string,
  credentials: OwnYouVerifiableCredential[] = [],
): Promise<ExtensionCredentialSyncBasePayload> {
  // Slice 0 / AT-16 (ADR 0005 Q6 + Q6.tail) — the wire-builder reads
  // structured records from `NS.iabClassifications` via paginated loop and
  // runs them through the explicit, multi-stage, policy-driven selection
  // pipeline:
  //
  //   paginate → drop-record + shape filter → per-observation eligibility →
  //   aggregate → per-aggregate eligibility → disclosure_confidence (v1) →
  //   signal_score_v1 → diversity controls → privacy budget → projection.
  //
  // The pipeline supersedes the AT-05/AT-06 single-call read with the
  // 200-record silent cap (Finding 3). Quality (which signals to disclose)
  // and privacy (how much information is on the wire) are now split into
  // separate stages; caps are a privacy budget, not a quality knob.
  //
  // SECURITY: selection metadata (`score`, `rank`, `reason_codes`) is
  // store/wallet-LOCAL — `projectToClaimSet` strips it before the wire
  // payload is assembled. The wallet's leak-guard validator hard-rejects
  // any payload that carries it.
  //
  // The legacy `pickSection` / `pickCategory` / `pickDemographicKey` /
  // `pickDemographicValue` / `pickConfidence` / `mapToSortedClaims` helpers
  // remain in the file as the unit-shape primitives for the existing
  // section-router unit tests; they're inlined here only as the structural
  // shape gate the pipeline's `applyDropAndShapeFilter` complements.
  const records = (await paginateClassifications(store, userId)) as DisclosureFactRecord[];

  const { facts } = runSelectionPipeline(records, {
    policy: DISCLOSURE_POLICY,
    resolveCanonicalKey: (g) => groupingToField[g],
  });

  const projected = projectToClaimSet(facts, CANONICAL_TAXONOMY);

  // The projected shape produces alphabetically-sorted lists per family
  // already. Demographics whitelist filtering happens implicitly via the
  // canonical-key resolver: `groupingToField` only maps grouping labels
  // that have a canonical demographic key, and the AT-19 work expands
  // the schema. Records whose canonical key isn't in
  // `policy.canonical_demographics` are kept by the resolver but rejected
  // by the encoder + wallet — this preserves the existing strict shape
  // gate while AT-16's pipeline owns the per-family quality gates.
  const canonicalDemographicsSet = new Set(Object.keys(DISCLOSURE_POLICY.canonical_demographics));
  const demographics: Record<string, string> = {};
  const demographicsConfidence: Record<string, number> = {};
  for (const [k, v] of Object.entries(projected.demographics)) {
    if (!canonicalDemographicsSet.has(k)) continue;
    demographics[k] = v;
    const c = projected.demographics_confidence[k];
    if (typeof c === 'number' && Number.isFinite(c)) {
      demographicsConfidence[k] = c;
    }
  }

  const claimSet: NormalizedClaimSet = {
    interests: projected.interests,
    purchase_intent: projected.purchase_intent,
    demographics,
    demographics_confidence: demographicsConfidence,
    household: {},
    reputation_level: computeReputationLevel(credentials, REPUTATION_POLICY),
  };

  return {
    claimSet,
    credentials,
    preferences: readDisclosurePreferences(),
    disclosurePolicy: DISCLOSURE_POLICY,
  };
}

/**
 * Derive the wallet identity material the extension stores. Each piece is
 * domain-separated from the others (and from the user's encryption keys)
 * via EIP-712 purpose strings inside @ownyou/identity's KeyDerivation.
 *
 * Sprint 18c remediation, closes G2: the extension is a dumb store; all
 * keys originate here.
 */
async function deriveBbsSyncMaterial(
  signer: WalletSigner,
  claimSet: NormalizedClaimSet,
): Promise<{
  nymSecret: Uint8Array;
  bbsKeyPair: { secretKey: Uint8Array; publicKey: Uint8Array };
  bbsSignature: Uint8Array;
}> {
  const keyDerivation = new KeyDerivation();
  const [nymSecret, bbsCredentialSeed] = await Promise.all([
    keyDerivation.deriveNymSecret(signer),
    keyDerivation.deriveBBSCredentialSeed(signer),
  ]);
  const suite = createBLS12381Suite();
  const bbsKeyPair = await suite.bbsGenerateKeyPair(bbsCredentialSeed);
  const bbsSignature = await issueCredential(suite, claimSet, bbsKeyPair);
  return { nymSecret, bbsKeyPair, bbsSignature };
}

export async function syncCredentialsToExtension(params: {
  store: MemoryStore;
  userId: string;
  walletSigner: WalletSigner;
  credentials?: OwnYouVerifiableCredential[];
  force?: boolean;
  storage?: Storage;
}): Promise<ExtensionCredentialSyncStatus> {
  const storage = params.storage ?? localStorage;

  // Slice-1: install-consent is the SOLE authoritative gate per design
  // §4.6 + Demo 5. The extension's `wallet-module.handleRequestDisclosure`
  // refuses to issue grants without `attributionConsent::install` in
  // chrome.storage.local, which is the protocol-level enforcement.
  //
  // The legacy `isBbsSharingEnabled` localStorage gate (Sprint-14 era,
  // pre-Q7) doubled the gate and stalled the credential-sync path for
  // every user who completed install-consent but never visited the
  // Sprint-14 privacy-toggles UI to flip the legacy flag. With AT-13
  // install-consent recording the user's intent at protocol level, this
  // PWA-side localStorage gate is redundant — removing it.
  //
  // The function `isBbsSharingEnabled` itself is kept (exported) for
  // backwards compatibility with Settings.tsx reads, but no longer gates
  // credential sync. The post-walk remediation pass owns deleting it
  // entirely + reworking the privacy-toggles UI to derive its state from
  // `attributionConsent::install`.

  if (typeof window === 'undefined' || !window.OwnYouCredentialSync) {
    const status = { state: 'unavailable' as const, message: 'OwnYou extension credential sync API unavailable' };
    writeStatus(status, storage);
    return status;
  }

  try {
    const basePayload = await buildExtensionCredentialSyncPayload(
      params.store,
      params.userId,
      params.credentials ?? [],
    );
    const { nymSecret, bbsKeyPair, bbsSignature } = await deriveBbsSyncMaterial(
      params.walletSigner,
      basePayload.claimSet,
    );
    const payload: ExtensionCredentialSyncPayload = {
      ...basePayload,
      nymSecret: Array.from(nymSecret),
      bbsKeyPair: {
        secretKey: Array.from(bbsKeyPair.secretKey),
        publicKey: Array.from(bbsKeyPair.publicKey),
      },
      bbsSignature: Array.from(bbsSignature),
    };
    const digest = stableDigest(payload);
    const digestKey = `${SYNC_DIGEST_PREFIX}${params.userId}`;

    if (!params.force && storage.getItem(digestKey) === digest) {
      const extensionStatus = await window.OwnYouCredentialSync.getStatus().catch(() => null);
      if (extensionStatus?.hasKeys && (extensionStatus.credentialCount ?? 0) > 0) {
        const status = {
          state: 'synced' as const,
          message: 'Extension credentials already current',
          syncedAt: extensionStatus.claimSetUpdatedAt ?? Date.now(),
          claimCount: payload.claimSet.interests.length + payload.claimSet.purchase_intent.length,
        };
        writeStatus(status, storage);
        return status;
      }
    }

    const response = await window.OwnYouCredentialSync.push(payload);
    if (response?.success === false) {
      throw new Error(response.error || 'Extension credential sync failed');
    }

    storage.setItem(digestKey, digest);
    const status = {
      state: 'synced' as const,
      message: 'Extension credentials synced',
      syncedAt: Date.now(),
      claimCount: payload.claimSet.interests.length + payload.claimSet.purchase_intent.length,
    };
    writeStatus(status, storage);
    setupReactiveResync(params, storage);
    return status;
  } catch (err) {
    const status = {
      state: 'error' as const,
      message: err instanceof Error ? err.message : String(err),
    };
    writeStatus(status, storage);
    return status;
  }
}

// ── F6 reactivity (Sprint 18c remediation Phase 6) ──────────────────────
//
// After a successful sync, watch for two classes of change that should
// trigger a re-sync:
//   1. New writes to NS.iabClassifications for the same userId — i.e. the
//      reconciler produced a new structured classification record.
//   2. Disclosure preference changes (localStorage `ownyou_bbs_*` keys or
//      `ownyou_privacy_toggles`) — i.e. the user changed settings.
//
// Implementation note: MemoryStore has no built-in subscription API, so
// we instrument `store.put` once per store instance and dispatch a custom
// window event after each write.
//
// Cross-tab vs in-tab preferences (fifth-pass review M3): the browser only
// fires `storage` events in OTHER tabs, not the one that called
// localStorage.setItem. So a settings UI that writes a preference and
// expects same-tab reactivity must use the `setDisclosurePreference`
// helper exported below — it dispatches a custom in-tab event in
// addition to writing localStorage. We listen for both the cross-tab
// `storage` event and the in-tab `ownyou:preference-changed` event.
//
// This whole reactivity layer is transitional. The cleaner long-term shape
// is a first-class notification API on MemoryStore, but that's a wider
// change than this remediation should pull in.

const STORE_NAMESPACE_CHANGED_EVENT = 'ownyou:store-namespace-changed';
const PREFERENCE_CHANGED_EVENT = 'ownyou:preference-changed';

/**
 * Set a disclosure preference. Use this from the consumer settings UI
 * (or any other code that mutates `ownyou_bbs_*` / `ownyou_privacy_toggles`)
 * so that same-tab listeners — including the credential-sync reactivity
 * path — pick up the change. `localStorage.setItem` alone only fires the
 * `storage` event in OTHER tabs.
 */
export function setDisclosurePreference(
  key: string,
  value: string,
  storage: Storage = localStorage,
): void {
  storage.setItem(key, value);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PREFERENCE_CHANGED_EVENT, { detail: { key } }));
  }
}
const instrumentedStores = new WeakSet<MemoryStore>();
// Subscriptions are scoped per store so that re-binding the same userId to a
// different store reference (notably in tests, where each test builds a
// fresh in-memory MemoryStore) installs fresh listeners against the new
// store rather than reusing stale ones bound to a previous instance.
const activeReactiveSyncs = new WeakMap<MemoryStore, Map<string, () => void>>();

interface NamespaceChangedDetail {
  namespace: readonly [string, string];
}

function instrumentStoreForReactivity(store: MemoryStore): void {
  if (instrumentedStores.has(store)) return;
  instrumentedStores.add(store);
  const originalPut = store.put.bind(store);
  // The MemoryStore type is `class`, so we override on the instance.
  (store as unknown as { put: typeof originalPut }).put = (async (
    namespace,
    key,
    value,
  ) => {
    const result = await originalPut(namespace, key, value);
    if (typeof window !== 'undefined' && Array.isArray(namespace) && namespace.length >= 2) {
      const detail: NamespaceChangedDetail = {
        namespace: [namespace[0] as string, namespace[1] as string] as const,
      };
      window.dispatchEvent(new CustomEvent(STORE_NAMESPACE_CHANGED_EVENT, { detail }));
    }
    return result;
  }) as typeof originalPut;
}

function setupReactiveResync(
  params: {
    store: MemoryStore;
    userId: string;
    walletSigner: WalletSigner;
    credentials?: OwnYouVerifiableCredential[];
    storage?: Storage;
  },
  storage: Storage,
): void {
  if (typeof window === 'undefined') return;

  let perStoreSubs = activeReactiveSyncs.get(params.store);
  if (!perStoreSubs) {
    perStoreSubs = new Map<string, () => void>();
    activeReactiveSyncs.set(params.store, perStoreSubs);
  }
  if (perStoreSubs.has(params.userId)) return;

  instrumentStoreForReactivity(params.store);

  const triggerResync = (): void => {
    void syncCredentialsToExtension({
      store: params.store,
      userId: params.userId,
      walletSigner: params.walletSigner,
      credentials: params.credentials,
      force: false,
      storage,
    }).catch(() => {
      // Re-sync errors are surfaced via the status writer inside
      // syncCredentialsToExtension; nothing further to do here.
    });
  };

  const onNamespaceChange = (event: Event): void => {
    const detail = (event as CustomEvent<NamespaceChangedDetail>).detail;
    if (!detail || !Array.isArray(detail.namespace)) return;
    const [ns, nsUserId] = detail.namespace;
    if (nsUserId !== params.userId) return;
    if (ns !== 'ownyou.iab') return;
    triggerResync();
  };

  const onStorage = (event: StorageEvent): void => {
    if (typeof event.key !== 'string') return;
    if (!event.key.startsWith('ownyou_bbs_') && event.key !== 'ownyou_privacy_toggles') return;
    triggerResync();
  };

  // Same-tab in-window event from `setDisclosurePreference`.
  // Required because the browser does not fire `storage` for
  // `localStorage.setItem` calls made in the same tab/window.
  const onPreferenceChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ key: string }>).detail;
    if (!detail || typeof detail.key !== 'string') return;
    if (!detail.key.startsWith('ownyou_bbs_') && detail.key !== 'ownyou_privacy_toggles') return;
    triggerResync();
  };

  window.addEventListener(STORE_NAMESPACE_CHANGED_EVENT, onNamespaceChange);
  window.addEventListener('storage', onStorage);
  window.addEventListener(PREFERENCE_CHANGED_EVENT, onPreferenceChange);

  perStoreSubs.set(params.userId, () => {
    window.removeEventListener(STORE_NAMESPACE_CHANGED_EVENT, onNamespaceChange);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(PREFERENCE_CHANGED_EVENT, onPreferenceChange);
    perStoreSubs!.delete(params.userId);
  });
}

/** Tear down the reactivity subscription for (store, userId). Used by the
 *  hook on unmount, and by tests that want to verify clean isolation. */
export function stopReactiveSyncForUser(store: MemoryStore, userId: string): void {
  const perStoreSubs = activeReactiveSyncs.get(store);
  const cleanup = perStoreSubs?.get(userId);
  if (cleanup) cleanup();
}

// `readExtensionCredentialSyncStatus` was a previously-exported reader for
// the `${STATUS_KEY}` localStorage entry. Ninth-pass review found zero
// production callers (only the definition existed). Removed rather than
// preserving a dead helper. If a future settings UI needs to display
// sync status, the recommended path is to subscribe to the
// `ownyou:extension-credential-sync` CustomEvent dispatched by
// `writeStatus` below — push, not pull.

function writeStatus(status: ExtensionCredentialSyncStatus, storage: Storage): void {
  storage.setItem(STATUS_KEY, JSON.stringify(status));
  window.dispatchEvent(new CustomEvent('ownyou:extension-credential-sync', { detail: status }));
}

// Slice 0 / AT-16: the legacy `pickCategory` / `pickConfidence` /
// `pickSection` / `pickDemographicKey` / `pickDemographicValue` /
// `mapToSortedClaims` helpers (introduced AT-05 / AT-06) were the
// per-record shape readers in the pre-pipeline wire-builder. With the
// pipeline (`apps/consumer/src/services/wire-builder/selection-pipeline.ts`)
// owning routing + aggregation + scoring + diversity + privacy budget,
// these helpers are dead. The shape gates they enforced now live in:
//   - pipeline.applyDropAndShapeFilter (section discriminant)
//   - pipeline.applyPerObservationEligibility (confidence shape + min)
//   - pipeline.aggregateObservations (taxonomy_id + grouping_value resolution)
//   - pipeline.projectToClaimSet (deterministic ordering)
// The exported `DEFAULT_AUDIENCE_TAXONOMY_VALIDATOR` / `idPattern` /
// `maxIdLength` constants are kept so external callers (and the AT-10
// CI guard) continue to compile.

function readDisclosurePreferences(): ExtensionCredentialSyncPayload['preferences'] {
  return {
    excluded_categories: readStringArray('ownyou_bbs_excluded_categories'),
    demographics_granularity: readDemographicsGranularity(),
    reputation_enabled: localStorage.getItem('ownyou_bbs_reputation_enabled') !== 'false',
  };
}

function readStringArray(key: string): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function readDemographicsGranularity(): 'off' | 'standard' {
  const raw = localStorage.getItem('ownyou_bbs_demographics_granularity');
  return raw === 'off' ? 'off' : 'standard';
}

function stableDigest(payload: ExtensionCredentialSyncPayload): string {
  return JSON.stringify(payload);
}
